"use client"

import Link from "next/link"
import Image from "next/image"
import type { ReactNode } from "react"

// ---- Brand mark (HSVE logo image) ----
export function AuthLogo() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Image
        src="/images/hsve-logo.png"
        alt="HSVE Consultation Services"
        width={180}
        height={60}
        style={{ height: 50, width: "auto" }}
        priority
      />
    </div>
  )
}

// ---- Icons ----
export function MailIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  )
}

export function LockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

export function UserIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

export function EyeIcon({ off }: { off?: boolean }) {
  return off ? (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3 8 10 8a9.7 9.7 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  ) : (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

export function ArrowIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  )
}

// ---- Shell: centered card on grid backdrop ----
interface AuthShellProps {
  tag: string
  children: ReactNode
}

export function AuthShell({ tag, children }: AuthShellProps) {
  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "72px 24px",
        background: "var(--bg)",
        overflow: "hidden",
      }}
    >
      {/* grid + accent glow backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage:
            "radial-gradient(620px 360px at 50% -6%, var(--accent-soft), transparent 70%)," +
            "linear-gradient(to right, var(--border-soft) 1px, transparent 1px)," +
            "linear-gradient(to bottom, var(--border-soft) 1px, transparent 1px)",
          backgroundSize: "100% 100%, 56px 56px, 56px 56px",
          maskImage: "linear-gradient(to bottom, black, transparent 62%)",
          WebkitMaskImage: "linear-gradient(to bottom, black, transparent 62%)",
          opacity: 0.6,
        }}
      />

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 432,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <AuthLogo />
        </div>

        <div
          style={{
            width: "100%",
            background: "var(--bg-soft)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {/* mono tab strip — terminal breadcrumb */}
          <div
            className="font-mono"
            style={{
              padding: "10px 22px",
              fontSize: 11,
              color: "var(--muted)",
              borderBottom: "1px solid var(--border-soft)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "var(--accent)" }}>$</span> {tag}
          </div>
          <div style={{ padding: "30px 32px 28px" }}>{children}</div>
        </div>

        <div className="font-mono" style={{ marginTop: 26, fontSize: 12, color: "var(--muted)" }}>
          Need help?{" "}
          <Link href="/contact" style={{ color: "var(--text-2)", textDecoration: "none" }}>
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  )
}

// ---- Submit button ----
interface SubmitBtnProps {
  children: ReactNode
  loading?: boolean
  loadingText?: string
}

export function SubmitBtn({ children, loading, loadingText }: SubmitBtnProps) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="font-mono"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 9,
        padding: "13px 16px",
        fontSize: 14,
        fontWeight: 500,
        borderRadius: 6,
        marginTop: 4,
        background: "var(--accent)",
        color: "var(--accent-ink)",
        border: "none",
        cursor: loading ? "not-allowed" : "pointer",
        opacity: loading ? 0.7 : 1,
      }}
    >
      {loading ? (
        <>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              border: "2px solid var(--accent-ink)",
              borderTopColor: "transparent",
              animation: "spin 1s linear infinite",
            }}
          />
          {loadingText || "Loading..."}
        </>
      ) : (
        <>
          {children} <ArrowIcon />
        </>
      )}
    </button>
  )
}

// ---- Alt link footer ----
interface AltLinkProps {
  children: ReactNode
}

export function AltLink({ children }: AltLinkProps) {
  return (
    <div
      style={{
        marginTop: 24,
        paddingTop: 20,
        borderTop: "1px solid var(--border-soft)",
        textAlign: "center",
        fontSize: 13,
        color: "var(--text-2)",
      }}
    >
      {children}
    </div>
  )
}

// ---- CSS for spin animation ----
export function AuthStyles() {
  return (
    <style jsx global>{`
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `}</style>
  )
}
